import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Combobox, Datepicker, Input } from '@ibsys/kit';
import { useConversationsAttributes } from '@/entities/conversations';
import type { ConversationAttributeItem } from '@/entities/attributes/model/api/types';
import type { AttributeFilter, AttributeFilterDateRange, AttributeFilterNumeric } from '@/entities/dashboards';
import { AttributeFilterCell, AttributeFilterPicker, createEmptyFilter } from '@/features/attribute-filters';

/**
 * Значение сквозного фильтра «Стратега».
 * `filters` — атрибутные фильтры в формате бэка (см. {@link AttributeFilter}),
 * `dateFrom`/`dateTo` — границы периода в формате YYYY-MM-DD (или не заданы).
 */
export type StrategistFiltersValue = {
  filters: AttributeFilter[];
  dateFrom?: string;
  dateTo?: string;
};

type Props = {
  /** Текущее значение фильтра */
  value: StrategistFiltersValue;
  /** Колбэк обновления всего значения фильтра */
  onChange: (next: StrategistFiltersValue) => void;
};

/** Путь атрибута «Пол клиента» (TEXT). */
const GENDER_PATH = 'clientGender';
/** Путь атрибута «Дата рождения клиента» (DATE) — фильтруется как диапазон дат. */
const BIRTH_DATE_PATH = 'clientBirthDate';
/** Путь атрибута «Сумма страховой премии» (числовой). */
const INSURANCE_SUM_PATH = 'policy[0].insurancePremiumSum';
/** Пути, которыми владеют выделенные контролы — исключаются из ручного добавления. */
const OWNED_PATHS: ReadonlySet<string> = new Set([GENDER_PATH, BIRTH_DATE_PATH, INSURANCE_SUM_PATH]);

/** Значения атрибута `clientGender` (TEXT): ровно «Муж.» / «Жен.». */
const GENDER_MALE = 'Муж.';
const GENDER_FEMALE = 'Жен.';

/** Локальное состояние выделенных контролов (строки — как во input-полях). */
type DedicatedState = {
  gender: string;
  ageFrom: string;
  ageTo: string;
  sumFrom: string;
  sumTo: string;
};

const EMPTY_DEDICATED: DedicatedState = { gender: '', ageFrom: '', ageTo: '', sumFrom: '', sumTo: '' };

/** Форматирует дату в ISO `YYYY-MM-DD` по локальным компонентам (без сдвига таймзоны). */
const toIsoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Дата рождения человека, которому сегодня исполняется `age` лет. */
const birthDateForAge = (age: number): string => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - age);
  return toIsoDate(d);
};

/** Парсит строку числового поля; пустая/невалидная строка → undefined. */
const parseNum = (s: string): number | undefined => {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Собирает атрибутные фильтры выделенных контролов в формат аппликатора quality-dashboard.
 * Незаданные контролы не дают фильтра.
 */
const buildDedicatedFilters = (s: DedicatedState): AttributeFilter[] => {
  const out: AttributeFilter[] = [];

  // Пол → {path, text}
  if (s.gender) {
    out.push({ path: GENDER_PATH, text: s.gender });
  }

  // Возраст от/до → диапазон дат рождения относительно сегодня.
  // Старший возраст («до») → более ранняя дата рождения (нижняя граница dateFrom),
  // младший возраст («от») → более поздняя дата рождения (верхняя граница dateTo).
  const ageFrom = parseNum(s.ageFrom);
  const ageTo = parseNum(s.ageTo);
  if (ageFrom !== undefined || ageTo !== undefined) {
    const birth: AttributeFilterDateRange = { path: BIRTH_DATE_PATH };
    if (ageTo !== undefined) birth.dateFrom = birthDateForAge(ageTo);
    if (ageFrom !== undefined) birth.dateTo = birthDateForAge(ageFrom);
    out.push(birth);
  }

  // Сумма страховки от/до → {path, min?, max?}
  const sumMin = parseNum(s.sumFrom);
  const sumMax = parseNum(s.sumTo);
  if (sumMin !== undefined || sumMax !== undefined) {
    const sum: AttributeFilterNumeric = { path: INSURANCE_SUM_PATH };
    if (sumMin !== undefined) sum.min = sumMin;
    if (sumMax !== undefined) sum.max = sumMax;
    out.push(sum);
  }

  return out;
};

/**
 * Сквозной фильтр раздела «Стратег»: период (date range) + выделенные контролы
 * (пол, возраст от/до, сумма страховки от/до) + произвольные атрибутные фильтры.
 * Выделенные контролы «владеют» путями `clientGender` / `clientBirthDate` /
 * `policy[0].insurancePremiumSum`: эти пути исключены из ручного добавления, чтобы
 * в итоговом `filters` не было дублей одного пути. Переиспользует `AttributeFilterPicker`
 * и `AttributeFilterCell` из фичи attribute-filters. Наружу отдаёт
 * `{ filters, dateFrom, dateTo }` — готовое тело POST-запросов «Стратега».
 */
export const StrategistFilters: FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation('strategist');
  const [dedicated, setDedicated] = useState<DedicatedState>(EMPTY_DEDICATED);

  // Системная конфигурация атрибутов разговора — те же, что в гриде диалогов и дашборде качества.
  const { data: conversationAttributes } = useConversationsAttributes({ limit: 1000 });
  const availableAttributes: ConversationAttributeItem[] = useMemo(
    () => conversationAttributes?.attributes ?? [],
    [conversationAttributes?.attributes],
  );

  // Карта атрибутов по `path` — единый ключ совпадает с `path` в атрибутных фильтрах.
  const attributesByPath = useMemo(() => {
    const map = new Map<string, ConversationAttributeItem>();
    availableAttributes.forEach((a) => map.set(a.path, a));
    return map;
  }, [availableAttributes]);

  // Фильтры, добавленные вручную (без путей, которыми владеют выделенные контролы).
  const manualFilters = useMemo(() => value.filters.filter((f) => !OWNED_PATHS.has(f.path)), [value.filters]);

  // Выделенные пути скрыты из списка добавления, чтобы избежать дублей путей.
  const activePaths = useMemo(
    () => new Set<string>([...manualFilters.map((f) => f.path), ...OWNED_PATHS]),
    [manualFilters],
  );

  const genderOptions = useMemo(
    () => [
      { label: t('filters.genderAll'), value: '' },
      { label: t('filters.genderMale'), value: GENDER_MALE },
      { label: t('filters.genderFemale'), value: GENDER_FEMALE },
    ],
    [t],
  );

  /** Пересобирает итоговый массив фильтров (ручные + выделенные) и эмитит наружу. */
  const emitFilters = (nextManual: AttributeFilter[], nextDedicated: DedicatedState) => {
    onChange({ ...value, filters: [...nextManual, ...buildDedicatedFilters(nextDedicated)] });
  };

  const updateDedicated = (patch: Partial<DedicatedState>) => {
    const next = { ...dedicated, ...patch };
    setDedicated(next);
    emitFilters(manualFilters, next);
  };

  const handleAddAttribute = (path: string) => {
    const attribute = attributesByPath.get(path);
    if (!attribute) return;
    const newFilter = createEmptyFilter(attribute);
    if (!newFilter) return;
    emitFilters([...manualFilters, newFilter], dedicated);
  };

  const handleUpdateFilter = (next: AttributeFilter) => {
    emitFilters(
      manualFilters.map((f) => (f.path === next.path ? next : f)),
      dedicated,
    );
  };

  const handleRemoveFilter = (path: string) => {
    emitFilters(
      manualFilters.filter((f) => f.path !== path),
      dedicated,
    );
  };

  return (
    <Card>
      <CardContent className="p-4 flex flex-col gap-4">
        <div className="flex flex-wrap gap-4 items-end w-full">
          <Datepicker
            mode="range"
            outputType="iso-date"
            label={t('filters.period')}
            value={{ from: value.dateFrom, to: value.dateTo }}
            onChange={(v) => {
              const range = typeof v === 'object' && v !== null ? v : { from: undefined, to: undefined };
              onChange({ ...value, dateFrom: range.from || undefined, dateTo: range.to || undefined });
            }}
          />

          <Combobox
            label={t('filters.gender')}
            className="w-40"
            value={dedicated.gender}
            onChange={(v) => updateDedicated({ gender: v == null ? '' : String(v) })}
            options={genderOptions}
            isSearchable={false}
          />

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-foreground">{t('filters.age')}</span>
            <div className="flex gap-2">
              <Input
                type="number"
                className="w-24"
                placeholder={t('filters.from')}
                value={dedicated.ageFrom}
                onChange={(e) => updateDedicated({ ageFrom: e.target.value })}
              />
              <Input
                type="number"
                className="w-24"
                placeholder={t('filters.to')}
                value={dedicated.ageTo}
                onChange={(e) => updateDedicated({ ageTo: e.target.value })}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-foreground">{t('filters.insuranceSum')}</span>
            <div className="flex gap-2">
              <Input
                type="number"
                className="w-28"
                placeholder={t('filters.from')}
                value={dedicated.sumFrom}
                onChange={(e) => updateDedicated({ sumFrom: e.target.value })}
              />
              <Input
                type="number"
                className="w-28"
                placeholder={t('filters.to')}
                value={dedicated.sumTo}
                onChange={(e) => updateDedicated({ sumTo: e.target.value })}
              />
            </div>
          </div>

          <AttributeFilterPicker
            availableAttributes={availableAttributes}
            activePaths={activePaths}
            onAdd={handleAddAttribute}
          />
        </div>

        {manualFilters.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 items-end">
            {manualFilters.map((filter) => (
              <AttributeFilterCell
                key={filter.path}
                attribute={attributesByPath.get(filter.path)}
                filter={filter}
                onChange={handleUpdateFilter}
                onRemove={() => handleRemoveFilter(filter.path)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
